import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { AIRPORTS as MAJOR_AIRPORTS, getAirportByCode, nearestAirport, type Airport } from '../data/airports';

/** How the origin was found. `fallback` means detection failed and the
 * value is a default, not a guess about the traveller at all. */
export type OriginSource = 'manual' | 'browser' | 'ip' | 'fallback';

export type OriginValue = {
    city: string;
    airport: string;
    source: OriginSource;
    /** Chosen by the traveller in the location picker. A pinned origin is
     * kept across visits and never overwritten by detection. */
    pinned?: boolean;
    _v?: number;
};

export type DeviceLocationResult = 'ok' | 'denied' | 'unavailable';

type OriginContextValue = {
    origin: OriginValue | null;
    setOrigin: (o: Omit<OriginValue, '_v'> | OriginValue | null) => void;
    loading: boolean;
    /** Pins an airport the traveller picked from the list. */
    chooseAirport: (airport: Airport) => void;
    /** Asks the browser for the traveller's position and pins the nearest airport. */
    locateWithDevice: () => Promise<DeviceLocationResult>;
    /** Drops the pinned origin and detects one again. */
    resetToAutomatic: () => void;
};

type OriginProviderProps = {
    children: React.ReactNode;
};

const OriginContext = createContext<OriginContextValue | null>(null);

const STORAGE_KEY = 'muno_origin';
const STORAGE_VERSION = 3; // bump to force re-detection for existing users

/**
 * The saved origin, if it is one the traveller chose. Detected values are
 * saved too but not trusted on the next visit — the traveller may have
 * moved, so those are detected again.
 */
export function readStoredOrigin(raw: string | null): OriginValue | null {
    if (!raw) return null;
    try {
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return null;
        if (parsed._v !== STORAGE_VERSION || parsed.pinned !== true) return null;
        // An airport since dropped from the list can't back a flight search.
        if (!getAirportByCode(String(parsed.airport || ''))) return null;
        return parsed as OriginValue;
    } catch {
        return null;
    }
}

/* The Geolocation API's own `timeout` only starts once the permission
   prompt is answered, so an ignored prompt left the pill on "Locating…"
   for good and the IP lookup below never ran. The timer gives up at the
   same deadline whether or not the prompt was answered. */
const getPosition = (options: PositionOptions) =>
    new Promise<GeolocationPosition>((resolve, reject) => {
        const timer = window.setTimeout(() => reject(new Error('No position in time')), options.timeout ?? 10000);
        navigator.geolocation.getCurrentPosition(
            (pos) => { window.clearTimeout(timer); resolve(pos); },
            (err) => { window.clearTimeout(timer); reject(err); },
            options,
        );
    });

export const OriginProvider = ({ children }: OriginProviderProps) => {
    const [origin, setOriginState] = useState<OriginValue | null>(() => {
        try {
            return readStoredOrigin(localStorage.getItem(STORAGE_KEY));
        } catch {
            return null;
        }
    });
    const [loading, setLoading] = useState(!origin);
    /* Each detection run gets a number. Picking a location bumps it, so a
       detection still in flight can't overwrite what the traveller chose. */
    const runRef = useRef(0);

    const setOrigin = useCallback((o: Omit<OriginValue, '_v'> | OriginValue | null) => {
        const val = o ? { ...o, _v: STORAGE_VERSION } : null;
        setOriginState(val);
        try {
            if (val) localStorage.setItem(STORAGE_KEY, JSON.stringify(val));
            else localStorage.removeItem(STORAGE_KEY);
        } catch { /* storage blocked — the choice still holds for this visit */ }
    }, []);

    /* Find airport by city name (case-insensitive) */
    const airportByCity = (cityName: string) => {
        if (!cityName) return null;
        const lower = cityName.toLowerCase();
        return MAJOR_AIRPORTS.find(ap => ap.city.toLowerCase() === lower) || null;
    };

    const detect = useCallback(async () => {
        const run = ++runRef.current;
        const stale = () => run !== runRef.current;
        setLoading(true);

        const settle = (ap: Airport, source: OriginSource) => {
            if (stale()) return;
            setOrigin({ city: ap.city, airport: ap.code, source });
            setLoading(false);
        };

        // 1) Try browser geolocation
        if (navigator.geolocation) {
            try {
                const pos = await getPosition({ timeout: 6000, maximumAge: 300000 });
                if (stale()) return;
                settle(nearestAirport(pos.coords.latitude, pos.coords.longitude), 'browser');
                return;
            } catch { /* permission denied or timeout — fall through */ }
        }

        // 2) IP-based lookup (primary: ipapi.co)
        if (stale()) return;
        try {
            const controller = new AbortController();
            const timer = setTimeout(() => controller.abort(), 4000);
            const resp = await fetch('https://ipapi.co/json/', { signal: controller.signal });
            clearTimeout(timer);
            if (stale()) return;
            if (resp.ok) {
                const data = await resp.json();
                if (data.latitude && data.longitude) {
                    settle(nearestAirport(data.latitude, data.longitude), 'ip');
                    return;
                }
                // If no lat/lon but city is available, try matching by name
                const ap = airportByCity(data.city);
                if (ap) {
                    settle(ap, 'ip');
                    return;
                }
            }
        } catch { /* network error — fall through */ }

        // 3) IP-based lookup (backup: ip-api.com)
        if (stale()) return;
        try {
            const controller = new AbortController();
            const timer = setTimeout(() => controller.abort(), 4000);
            const resp = await fetch('http://ip-api.com/json/?fields=lat,lon,city', { signal: controller.signal });
            clearTimeout(timer);
            if (stale()) return;
            if (resp.ok) {
                const data = await resp.json();
                if (data.lat && data.lon) {
                    settle(nearestAirport(data.lat, data.lon), 'ip');
                    return;
                }
                const ap = airportByCity(data.city);
                if (ap) {
                    settle(ap, 'ip');
                    return;
                }
            }
        } catch { /* fall through */ }

        // 4) Final fallback
        const fallback = getAirportByCode('JFK');
        if (fallback) settle(fallback, 'fallback');
    }, [setOrigin]);

    /* Auto-detect on every load unless the traveller pinned a location */
    useEffect(() => {
        if (origin?.pinned) return;
        detect();
        // Unmounting abandons the run the same way a pick does.
        return () => { runRef.current += 1; };
    }, []);

    const chooseAirport = useCallback((airport: Airport) => {
        runRef.current += 1;
        setOrigin({ city: airport.city, airport: airport.code, source: 'manual', pinned: true });
        setLoading(false);
    }, [setOrigin]);

    const locateWithDevice = useCallback(async (): Promise<DeviceLocationResult> => {
        if (!navigator.geolocation) return 'unavailable';
        try {
            // A fresh fix: the traveller is asking because the old one is wrong.
            const pos = await getPosition({ timeout: 10000, maximumAge: 0, enableHighAccuracy: false });
            const ap = nearestAirport(pos.coords.latitude, pos.coords.longitude);
            runRef.current += 1;
            setOrigin({ city: ap.city, airport: ap.code, source: 'browser', pinned: true });
            setLoading(false);
            return 'ok';
        } catch (error) {
            return (error as GeolocationPositionError | undefined)?.code === 1 ? 'denied' : 'unavailable';
        }
    }, [setOrigin]);

    const resetToAutomatic = useCallback(() => {
        setOrigin(null);
        detect();
    }, [setOrigin, detect]);

    const value = { origin, setOrigin, loading, chooseAirport, locateWithDevice, resetToAutomatic };

    return <OriginContext.Provider value={value}>{children}</OriginContext.Provider>;
};

export const useOrigin = () => {
    const ctx = useContext(OriginContext);
    if (!ctx) throw new Error('useOrigin must be used within OriginProvider');
    return ctx;
};
